import React from "react";
import {
  Archive,
  Building,
  Calendar,
  Eye,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import { getUserInitials } from "../../utils/avatarUtils";

// ===== RESPONSIVE ARCHIVE MODAL COMPONENT =====
const ArchiveModal = ({
  styles,
  text,
  language,
  theme,
  screenSize,
  fonts,
  spacing,
  archivedEmployees,
  archivedCount,
  employeeProfilePics,
  onClose,
  onRestore,
  onView,
  onRestoreAll,
  onDeleteAll,
}) => (
  <div
    style={styles.modal}
    onClick={onClose}
  >
    <div
      style={styles.modalContent1}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Modal Header */}
      <div style={styles.modalHeader}>
        <button
          style={styles.closeBtn}
          onClick={onClose}
        >
          <X size={screenSize === "xs" ? 20 : 24} />
        </button>
      </div>

      {/* Archive Description */}
      <div
        style={{
          background:
            theme === "dark"
              ? "rgba(107, 114, 128, 0.1)"
              : "rgba(107, 114, 128, 0.05)",
          border: `1px solid ${theme === "dark"
            ? "rgba(107, 114, 128, 0.3)"
            : "rgba(107, 114, 128, 0.2)"
            }`,
          borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
          padding: spacing.padding,
          marginBottom: spacing.margin,
          display: "flex",
          alignItems: "center",
          gap: spacing.gap,
          flexDirection: screenSize === "xs" ? "column" : "row",
          textAlign: screenSize === "xs" ? "center" : "left",
        }}
      >
        <Archive size={screenSize === "xs" ? 18 : 20} color="#6b7280" />
        <div
          style={{
            color:
              theme === "dark"
                ? "rgba(255, 255, 255, 0.8)"
                : "rgba(26, 26, 26, 0.7)",
            fontSize: fonts.body,
            lineHeight: "1.5",
          }}
        >
          {text[language].archivedDescription}
        </div>
      </div>

      {/* Archive Statistics */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: screenSize === "xs" ? "1fr" : "repeat(2, 1fr)",
          gap: spacing.gap,
          marginBottom: spacing.margin,
        }}
      >
        <div
          style={{
            background:
              theme === "dark"
                ? "rgba(107, 114, 128, 0.1)"
                : "rgba(107, 114, 128, 0.05)",
            padding: spacing.padding,
            borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: "#6b7280",
              fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
              fontWeight: "700",
              marginBottom: "0.25rem",
            }}
          >
            {archivedCount}
          </div>
          <div
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.7)"
                  : "rgba(26, 26, 26, 0.6)",
              fontSize: fonts.small,
              fontWeight: "500",
            }}
          >
            {text[language].totalArchived}
          </div>
        </div>
        <div
          style={{
            background:
              theme === "dark"
                ? "rgba(34, 197, 94, 0.1)"
                : "rgba(34, 197, 94, 0.05)",
            padding: spacing.padding,
            borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: "#22c55e",
              fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
              fontWeight: "700",
              marginBottom: "0.25rem",
            }}
          >
            {
              archivedEmployees.filter(
                (emp) =>
                  emp.archivedDate &&
                  new Date(emp.archivedDate) >
                  new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
              ).length
            }
          </div>
          <div
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.7)"
                  : "rgba(26, 26, 26, 0.6)",
              fontSize: fonts.small,
              fontWeight: "500",
            }}
          >
            {text[language].archivedThisMonth}
          </div>
        </div>
      </div>

      {/* Archived Employees Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>{text[language].employee}</th>
              {screenSize !== "xs" && (
                <th style={styles.tableHeader}>
                  {text[language].department}
                </th>
              )}
              {screenSize !== "xs" && screenSize !== "sm" && (
                <th style={styles.tableHeader}>{text[language].company}</th>
              )}
              <th style={styles.tableHeader}>
                {text[language].archivedDate}
              </th>
              <th style={styles.tableHeader}>{text[language].actions}</th>
            </tr>
          </thead>
          <tbody>
            {archivedEmployees.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    screenSize === "xs"
                      ? "3"
                      : screenSize === "sm"
                        ? "4"
                        : "5"
                  }
                  style={{
                    ...styles.tableCell,
                    textAlign: "center",
                    padding: screenSize === "xs" ? "2rem" : "2.5rem",
                    color:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.5)"
                        : "rgba(26, 26, 26, 0.5)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: spacing.gap,
                    }}
                  >
                    <Archive
                      size={screenSize === "xs" ? 32 : 48}
                      color={
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.3)"
                          : "rgba(26, 26, 26, 0.3)"
                      }
                    />
                    <div>{text[language].noArchivedEmployees}</div>
                    <div style={{ fontSize: fonts.small, opacity: "0.7" }}>
                      {text[language].employeesArchivedMessage}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              archivedEmployees.map((emp) => (
                <tr
                  key={emp.id}
                  style={{
                    ...styles.tableRow,
                    background:
                      theme === "dark"
                        ? "rgba(107, 114, 128, 0.05)"
                        : "rgba(107, 114, 128, 0.02)",
                  }}
                >
                  {/* Employee Info */}
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.gap,
                      }}
                    >
                      {/* Profile Picture */}
                      {employeeProfilePics[emp.id] ? (
                        <img
                          src={employeeProfilePics[emp.id]}
                          alt={emp?.name || "Employee"}
                          style={{
                            ...styles.userAvatar,
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling?.style) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}

                      <div
                        style={{
                          ...styles.userAvatar,
                          display: employeeProfilePics[emp.id] ? 'none' : 'flex'
                        }}
                      >
                        {getUserInitials(emp?.name)}
                      </div>

                      {/* Employee Name and Email */}
                      <div>
                        <div style={{ fontWeight: "600", fontSize: fonts.body }}>
                          {emp?.name?.split(" ").slice(0, 2).join(" ") || "N/A"}
                        </div>
                        <div style={{ fontSize: fonts.small, opacity: "0.7" }}>
                          {screenSize === "xs"
                            ? (emp?.email || "N/A").substring(0, 20) + "..."
                            : (emp?.email || "N/A")}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Department - Hidden on mobile */}
                  {screenSize !== "xs" && (
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <Building size={14} style={{ opacity: "0.6" }} />
                        {emp.department}
                      </div>
                    </td>
                  )}

                  {/* Company - Hidden on mobile and tablet */}
                  {screenSize !== "xs" && screenSize !== "sm" && (
                    <td style={styles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <Building size={14} style={{ opacity: "0.6" }} />
                        {emp.company}
                      </div>
                    </td>
                  )}

                  {/* Archived Date */}
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Calendar size={14} style={{ opacity: "0.6" }} />
                      <span style={{ fontSize: fonts.small }}>
                        {emp.archivedDate
                          ? new Date(emp.archivedDate).toLocaleDateString(
                            "en-US",
                            {
                              year:
                                screenSize === "xs" ? "2-digit" : "numeric",
                              month: "short",
                              day: "numeric",
                            }
                          )
                          : "N/A"}
                      </span>
                    </div>
                  </td>

                  {/* Actions */}
                  <td style={styles.tableCell}>
                    <div
                      style={{
                        display: "flex",
                        gap: screenSize === "xs" ? "0.25rem" : "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Restore Button */}
                      <button
                        style={{
                          ...styles.actionBtn,
                          ...styles.restoreBtn,
                        }}
                        title={text[language].restoreEmployee}
                        onClick={() => onRestore(emp.id)}
                      >
                        <RotateCcw size={screenSize === "xs" ? 12 : 14} />
                      </button>

                      {/* View Button */}
                      <button
                        style={{
                          ...styles.actionBtn,
                          ...styles.viewBtn,
                        }}
                        title={text[language].viewDetails}
                        onClick={() => onView(emp)}
                      >
                        <Eye size={screenSize === "xs" ? 12 : 14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Archive Actions */}
      {archivedEmployees.length > 0 && (
        <div
          style={{
            marginTop: spacing.margin,
            padding: spacing.padding,
            background:
              theme === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.02)",
            borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border:
              theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.05)",
            flexDirection: screenSize === "xs" ? "column" : "row",
            gap: screenSize === "xs" ? spacing.gap : "0",
          }}
        >
          <div
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.7)"
                  : "rgba(26, 26, 26, 0.6)",
              fontSize: fonts.body,
              textAlign: screenSize === "xs" ? "center" : "left",
            }}
          >
            {archivedEmployees.length}
            {archivedEmployees.length === 1
              ? text[language].archivedEmployee
              : text[language].archivedEmployees}
          </div>
          <div
            style={{
              display: "flex",
              gap: spacing.gap,
              flexWrap: "wrap",
              justifyContent: screenSize === "xs" ? "center" : "flex-end",
            }}
          >
            <button
              style={{
                ...styles.secondaryBtn,
                fontSize: fonts.small,
                padding:
                  screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
              }}
              onClick={onRestoreAll}
            >
              <RotateCcw size={screenSize === "xs" ? 12 : 14} />
              {screenSize !== "xs" && text[language].restoreAll}
            </button>
            <button
              style={{
                ...styles.secondaryBtn,
                fontSize: fonts.small,
                padding:
                  screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
                color: "#ef4444",
                borderColor: "rgba(239, 68, 68, 0.3)",
              }}
              onClick={onDeleteAll}
            >
              <Trash2 size={screenSize === "xs" ? 12 : 14} />
              {screenSize !== "xs" && text[language].deleteAll}
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
);

export default ArchiveModal;
